/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/explicit-module-boundary-types, sonarjs/no-duplicate-string */
import {
  CellClassParams,
  ColDef,
  EditableCallbackParams,
  ICellEditorParams,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import get from "lodash/get";
import omit from "lodash/omit";
import set from "lodash/set";
import { ComponentType, PropsWithChildren } from "react";

import {
  hasCurrentPlans,
  isValuePresent,
} from "../../../locs/requiredFields/validation";
import {
  CellEditorParams,
  CellSpec,
} from "../../../locs/shared/cellSpecs/types";
import { RfpBenefitPlan } from "../../../types/rfp.api.types";
import { getActiveEditorValuesFilter } from "../context/EditorValuesFilterContext";
import { getActiveCellEditorOverride } from "../context/CellEditorOverrideContext";

import { getCellValueInCurrentColumn } from "./agGridUtils";

import type { RfpCoverage } from "../../../types/rfp.api.types";
import type { BenefitValue } from "../types/values.types";

export interface CellDisplayParams {
  value: unknown;
  data?: ValueFormatterParams["data"];
  node?: ValueFormatterParams["node"];
  colDef?: ValueFormatterParams["colDef"];
  column?: ValueFormatterParams["column"];
  api?: ValueFormatterParams["api"];
}

/**
 * Resolves the appropriate cell spec based on cellVariants configuration
 *
 * If the benefit definition has cellVariants, this function:
 * 1. Gets the current value of the dependsOn field
 * 2. Looks up the corresponding cell spec in the options map
 * 3. Returns the matched cell spec, or the default if no match
 *
 * @param params - AG Grid cell parameters
 * @param benefit - Benefit definition that may contain cellVariants
 * @param baseCellSpec - The base cell spec to use if no cellVariants exist
 * @returns The resolved cell spec to use
 */
const resolveCellSpec = (
  params: any,
  benefit: any,
  baseCellSpec: CellSpec,
): CellSpec => {
  const cellVariants = benefit?.cellVariants;

  if (!cellVariants) {
    return baseCellSpec;
  }

  // Get the value of the field we depend on
  const dependentValue = getCellValueInCurrentColumn(
    params,
    cellVariants.dependsOn,
  );

  // If we have a match in options, use that cell spec
  if (dependentValue && cellVariants.options[dependentValue as string]) {
    return cellVariants.options[dependentValue as string];
  }

  // Fall back to default cell spec
  return cellVariants.default || baseCellSpec;
};

/**
 * CSS class name constants for grid columns and cells
 */
export const CLASSNAMES = {
  // Zebra striping column levels - actually relates to header group
  HEADER_COLUMN_LEVEL_0: "header-level-0",
  HEADER_COLUMN_LEVEL_1: "header-level-1",
  HEADER_COLUMN_LEVEL_2: "header-level-2",

  // Zebra column base and modifiers
  ZEBRA_COLUMN: "zebra-column",
  ZEBRA_COLUMN_PREFIX: "zebra-column--",
  ZEBRA_COLUMN_TYPE_PREFIX: "zebra-column-type--",

  // Benefit cell styling
  BENEFIT_CELL: "benefit-cell",
  BENEFIT_LABEL_CELL: "benefit-label-cell",
  BENEFIT_LABEL_CELL_NESTED: "benefit-label-cell--nested",

  // Cell state indicators
  CELL_VALIDATION_ERROR: "cell--validation-error",
  CELL_REQUIRED_MISSING: "cell--required-missing",
  CELL_INCOMPLETE_ARRAY: "cell--incomplete-array",

  // Header state indicators
  HEADER_REQUIRED_MISSING: "header--required-missing",

  // Excel export styling
  EXCEL_CENTER_ALIGN: "excel-center-align",
  EXCEL_PARENT_ROW: "excel-parent-row",

  // Even/odd identifiers for dynamic classes
  EVEN: "even",
  ODD: "odd",

  // Identifiers
  IDENTIFIER: "identifier",
  BENEFIT_KEY: "benefitKey",
} as const;

// DEFAULT VALUE FALLBACK:
export const DEFAULT = {
  HEADER_NAME: "Plan Group",
} as const;

export const cellRendererSelector = (params: ICellRendererParams) => {
  // const id = get(params, "data.meta.benefit.id", null);
  const baseCellSpec = get(params, "data.benefit.cell", {});
  const benefit = get(params, "data.benefit", {});

  // Resolve cell spec based on cellVariants if present
  const cell = resolveCellSpec(params, benefit, baseCellSpec);

  // Only use custom cell renderer if cellRenderer is a string (component name)
  // If it's a function, it's a formatter and should be handled by valueFormatter instead
  if (cell.cellRenderer && typeof cell.cellRenderer === "string") {
    return {
      component: cell.cellRenderer,
      params: {
        ...params,
        config: cell,
        cellEditorParams: get(cell, "cellEditorParams", {}),
      },
    };
  }

  return undefined;
};

export const cellEditorSelector = (params: ICellEditorParams) => {
  // Consumer-supplied editor override (e.g. Quotes typed-amount popover)
  // takes precedence over the cellSpec-driven default.
  const override = getActiveCellEditorOverride();
  if (override) {
    const result = override(params);
    if (result) {
      return {
        component: result.component,
        popup: result.popup ?? false,
        params: result.params ?? {},
      };
    }
  }

  const benefit = get(params, "data.benefit", {});
  const baseCellSpec = params.data?.propertyKey
    ? get(params, `data.benefit.properties.${params.data.propertyKey}.cell`, {})
    : get(params, "data.benefit.cell", {});

  // Resolve cell spec based on cellVariants if present
  const cell = resolveCellSpec(params, benefit, baseCellSpec);

  if (!cell.cellEditor) return undefined;

  const cellEditorParams = get(
    cell,
    "cellEditorParams",
    {},
  ) as CellEditorParams;

  // Dynamic params based on benefit key
  const benefitKey = params.data?.benefitKey;
  const dynamicParams: Record<string, any> = {};

  // For eligibilityClasses, pass available classes to the custom editor
  if (
    benefitKey === "eligibilityClasses" &&
    params.data?._coverage?.benefitClasses
  ) {
    const benefitClasses = params.data._coverage.benefitClasses;
    dynamicParams["availableClasses"] = benefitClasses;
  }

  // For rich select dropdowns with a static values array, replace the built-in
  // substring search with word-start filtering. This ensures typing "pe" matches
  // "Percentage" and "PEPM" but not "Heaped", while "funded" still matches
  // "Self Funded" (matches start of any word, case-insensitive).
  if (
    cell.cellEditor === "agRichSelectCellEditor" &&
    Array.isArray(cellEditorParams.values)
  ) {
    const formatValue = cellEditorParams.formatValue ?? ((v: string) => v);

    // Let the active surface hide values it must not offer for selection
    // (e.g. a sentinel enum member that belongs to a different tab). The
    // filter stays surface-agnostic here — the surface supplies the policy.
    const valuesFilter = getActiveEditorValuesFilter();
    const values = valuesFilter
      ? valuesFilter(cellEditorParams.values, params)
      : cellEditorParams.values;

    return {
      component: cell.cellEditor,
      popup: cell.popup || false,
      params: {
        ...dynamicParams,
        ...omit(cellEditorParams, ["availableClasses", "searchType"]),
        values,
        filterList: true,
        highlightMatch: true,
        searchType: "matchAny",
        // Prefix each word in the display text with \x01 (untypable) to
        // boost word-start matches in AG Grid's substring "matchAny" search.
        // Typing "pe" matches "\x01Percentage" via the \x01pe substring.
        // Note: this does NOT prevent mid-word matches — "pe" will also match
        // "Heaped" because AG Grid still does plain substring matching on the
        // original display text. The \x01 prefixes only add extra hit surface
        // at word boundaries.
        searchStringCreator: (values: string[]) =>
          values.map((v) =>
            formatValue(v)
              .split(/\s+/)
              .map((word) => `\x01${word}`)
              .join(""),
          ),
        validation: get(cell, "validation", {}),
      },
    };
  }

  return {
    component: cell.cellEditor,
    popup: cell.popup || false,
    params: {
      // Start with dynamic params
      ...dynamicParams,
      // Then spread cellEditorParams for other settings
      ...omit(cellEditorParams, ["availableClasses"]),
      // Add validation for custom editors that may need it
      validation: get(cell, "validation", {}),
    },
  };
};

/**
 * Extracts a meaningful string value from an object
 * Prevents [object Object] display by extracting known properties
 *
 * @param value - The object value to format
 * @returns A string representation of the object
 */
const formatObjectValue = (value: BenefitValue): string => {
  if ("type" in value) return String(value.type || "");
  if ("amount" in value) return String(value.amount || "");
  if ("age" in value) return String(value.age || "");
  if ("value" in value) return String(value.value || "");
  // Fallback to JSON string representation
  return JSON.stringify(value);
};

/**
 * Formats a cell value using the cell spec's valueFormatter if present
 * Always returns a string by coercing null/undefined to empty string and
 * converting all other values with String()
 *
 * @param params - AG Grid value formatter parameters
 * @param cell - Resolved cell spec
 * @returns The formatted string value (never null or undefined)
 */
const formatCellValue = (params: CellDisplayParams, cell: CellSpec): string => {
  // Try valueFormatter first, then cellRenderer (for backward compatibility)
  const formatter = cell.valueFormatter || cell.cellRenderer;

  if (!formatter) {
    // Handle null/undefined -> empty string
    if (params.value == null) {
      return "";
    }
    // Handle objects to prevent [object Object] display
    if (typeof params.value === "object") {
      return formatObjectValue(params.value as BenefitValue);
    }
    // Coerce to string for primitive values
    return String(params.value);
  }

  // Formatter should always be a function
  if (typeof formatter === "function") {
    const result = formatter({
      value: params.value,
      data: params.data,
      node: params.node,
      colDef: params.colDef,
      column: params.column,
      api: params.api,
    });
    // Ensure formatter result is also a string
    return result == null ? "" : String(result);
  }

  // If formatter is not a function, coerce raw value to string
  return params.value == null ? "" : String(params.value);
};

/**
 * Applies a suffix to a formatted value if the cell spec defines one
 * Only applies suffix to non-empty values that aren't the disabled indicator "-"
 *
 * @param formatted - The formatted string value
 * @param cell - Resolved cell spec that may contain a suffix
 * @returns The formatted value with suffix appended (if applicable)
 */
const applySuffixIfNeeded = (formatted: string, cell: CellSpec): string => {
  if (cell.suffix && formatted && formatted !== "-") {
    return `${formatted} ${cell.suffix}`;
  }
  return formatted;
};

/**
 * Applies a prefix to a formatted value if the cell spec defines one
 * Only applies prefix to non-empty values that aren't the disabled indicator "-"
 *
 * @param formatted - The formatted string value
 * @param cell - Resolved cell spec that may contain a prefix
 * @returns The formatted value with prefix prepended (if applicable)
 */
const applyPrefixIfNeeded = (formatted: string, cell: CellSpec): string => {
  if (cell.prefix && formatted && formatted !== "-") {
    return `${cell.prefix} ${formatted}`;
  }
  return formatted;
};

/**
 * Resolves the final display value for a cell.
 * This is shared between AG Grid's valueFormatter lifecycle and other
 * consumers, such as AI status renderers that need the same display text.
 *
 * @param params - Cell display parameters
 * @returns The final formatted string to display in the cell
 */
export const getFormattedCellDisplayValue = (
  params: CellDisplayParams,
): string => {
  const benefit = get(params, "data.benefit", {});
  const baseCellSpec = get(params, "data.benefit.cell", {});
  // Resolve cell spec based on cellVariants if present
  const cell = resolveCellSpec(params, benefit, baseCellSpec);

  // Check if this cell is disabled due to editableWhen conditions
  // If disabled, show "-" unless showValueWhenDisabled is set
  const isDisabled = isCellDisabled(params);
  if (isDisabled && !benefit?.editableWhen?.showValueWhenDisabled) {
    return "-";
  }

  // Format the cell value
  const formatted = formatCellValue(params, cell);

  // Apply suffix and prefix if the cell spec defines them
  return applyPrefixIfNeeded(applySuffixIfNeeded(formatted, cell), cell);
};

/**
 * Main value formatter for grid cells
 * Orchestrates cell value resolution, formatting, and suffix application
 *
 * @param params - AG Grid value formatter parameters
 * @returns The final formatted string to display in the cell
 */
export const valueFormatter = (params: ValueFormatterParams): string =>
  getFormattedCellDisplayValue(params);

export const isNetworked = (params: any) =>
  get(params, "data.benefit.networkDependent", false);

const _getValuePath = (params: any) => {
  const identifier = get(params, "colDef.field", "").split(".")[0].trim();
  const _isNetworked = isNetworked(params);
  const path = _isNetworked ? get(params, "colDef.field", "") : identifier;
  return path;
};

export const getValue = (params: any) => {
  const value = get(params.data, _getValuePath(params));
  return value;
};

export const valueGetter = (params: ValueGetterParams) => {
  // Get the benefit and base cell spec
  const benefit = get(params, "data.benefit", {});
  const baseCellSpec = params.data?.propertyKey
    ? get(params, `data.benefit.properties.${params.data.propertyKey}.cell`, {})
    : get(params, "data.benefit.cell", {});

  // Resolve cell spec based on cellVariants if present
  const cell = resolveCellSpec(params, benefit, baseCellSpec);

  // If cell spec has a custom valueGetter, use it
  if (typeof cell.valueGetter === "function") {
    return cell.valueGetter(params);
  }

  // Otherwise use default getValue
  return getValue(params);
};

export const valueSetter = (params: ValueSetterParams) => {
  const path = _getValuePath(params);
  if (params.newValue === undefined || !params.data) {
    return false;
  }

  // Get the benefit and base cell spec
  const benefit = get(params, "data.benefit", {});
  const baseCellSpec = params.data?.propertyKey
    ? get(params, `data.benefit.properties.${params.data.propertyKey}.cell`, {})
    : get(params, "data.benefit.cell", {});

  // Resolve cell spec based on cellVariants if present
  const cell = resolveCellSpec(params, benefit, baseCellSpec);

  // If cell spec has a custom valueSetter, use it
  if (typeof cell.valueSetter === "function") {
    return cell.valueSetter(params);
  }

  // Otherwise use default lodash set
  set(params.data, path, params.newValue);
  return true;
};

export const getCellClassesRules = (): Record<
  string,
  (params: CellClassParams) => boolean
> => ({
  [CLASSNAMES.CELL_VALIDATION_ERROR]: (params: CellClassParams) =>
    params.data?._validationState === "invalid",
  [CLASSNAMES.CELL_REQUIRED_MISSING]: (params: CellClassParams) => {
    if (!params.data?.benefit?.isRequired) return false;
    if (
      params.data.benefit.requiredOnCurrentCoverageOnly &&
      !hasCurrentPlans(params.data._coverage?.benefitPlans ?? [])
    )
      return false;
    if (isCellDisabled(params)) return false;
    return !isValuePresent(
      params.value,
      params.data?.benefit?.invalidValues,
      params.data?.benefit?.acceptableValues,
    );
  },
  [CLASSNAMES.CELL_INCOMPLETE_ARRAY]: (params: CellClassParams) => {
    const cellVariants = params.data?.benefit?.cellVariants;
    if (!cellVariants?.dependsOn) return false;

    const parentValue = getCellValueInCurrentColumn(
      params,
      cellVariants.dependsOn,
    );

    if (
      parentValue !== "multipleDollarAmounts" &&
      parentValue !== "multipleSalaryMultipliers"
    ) {
      return false;
    }

    const value = params.value;
    return !Array.isArray(value) || value.length === 0;
  },
});

interface EditableWhen {
  dependsOn: string;
  disabledValues: string[];
}

/**
 * Whether a field's editableWhen condition, on its own (ignoring cascading
 * disable from the parent field), disables the field. Required fields stay
 * editable even while the parent hasn't been answered yet — a required field
 * can't be both mandatory and unreachable.
 */
const isDisabledByOwnEditableWhen = (
  params: EditableCallbackParams,
  editableWhen: EditableWhen,
): boolean => {
  const dependentValue = getCellValueInCurrentColumn(
    params,
    editableWhen.dependsOn,
  );
  const parentUnanswered =
    dependentValue === undefined ||
    dependentValue === null ||
    dependentValue === "";

  return (
    (parentUnanswered && !params.data?.benefit?.isRequired) ||
    editableWhen.disabledValues.includes(dependentValue as string)
  );
};

/**
 * Whether the field the editableWhen depends on is itself disabled.
 * Creates chained dependency (e.g., controlField -> Type -> Amount).
 */
const isDisabledByParentField = (
  params: EditableCallbackParams,
  dependsOn: string,
): boolean => {
  if (!params.api || !params.colDef?.field) {
    return false;
  }

  let isDependentFieldDisabled = false;
  params.api.forEachNode((node: any) => {
    if (node.data?.benefitKey === dependsOn) {
      const dependentParams = { ...params, data: node.data, node };
      if (!isEditable(dependentParams)) {
        isDependentFieldDisabled = true;
      }
    }
  });

  return isDependentFieldDisabled;
};

export const isEditable = (params: EditableCallbackParams): boolean => {
  // First check standard read-only flag
  if (!params.data?.benefit || params.data.benefit.isReadOnly) {
    return false;
  }

  // Get the cell spec (check for propertyKey for nested properties)
  const cell = params.data?.propertyKey
    ? get(params, `data.benefit.properties.${params.data.propertyKey}.cell`, {})
    : get(params, "data.benefit.cell", {});

  // Priority 1: If cell spec has an editable function, use it
  if (typeof cell.editable === "function") {
    return cell.editable(params);
  }

  // Priority 2: Check if benefit definition has editableWhen configuration
  const editableWhen = params.data?.benefit?.editableWhen;
  if (
    editableWhen &&
    (isDisabledByOwnEditableWhen(params, editableWhen) ||
      isDisabledByParentField(params, editableWhen.dependsOn))
  ) {
    return false;
  }

  // Default to editable
  return true;
};

/**
 * Resolves the dependent value for an editableWhen dependency.
 *
 * Tries three strategies in order:
 * 1. Grid row lookup via getCellValueInCurrentColumn
 * 2. Direct node iteration fallback (when full grid context is unavailable)
 * 3. Plan-level property fallback from _coverage (for properties like policyPeriod
 *    that live on the benefit plan object rather than as a grid row)
 */
const resolveDependentValue = (params: any, dependsOn: string): unknown => {
  // Strategy 1: Row-based lookup via grid API
  if (params.api && params.colDef?.field) {
    const value = getCellValueInCurrentColumn(params, dependsOn);
    if (value !== undefined) return value;
  }

  // Strategy 2: Direct node iteration fallback
  if (!params.api && params.colDef?.field && params.node) {
    const api = params.node.gridApi;
    let found: unknown;
    if (api) {
      api.forEachNode((node: any) => {
        if (node.data?.benefitKey === dependsOn) {
          found = node.data[params.colDef.field];
        }
      });
    }
    if (found !== undefined) return found;
  }

  // Strategy 3: Plan-level property from _coverage
  if (params.data?._coverage?.benefitPlans && params.colDef?.field) {
    const planIdentifier = params.colDef.field.split(".")[0];
    const plan = params.data._coverage.benefitPlans.find(
      (bp: RfpBenefitPlan) =>
        bp.id === planIdentifier || bp.identifier === planIdentifier,
    );
    if (plan) {
      return (plan as Record<string, unknown>)[dependsOn];
    }
  }

  return undefined;
};

/**
 * Checks if a cell is currently disabled due to editableWhen conditions
 * This is used for conditional rendering (e.g., showing "-" instead of values)
 *
 * @param params - AG Grid cell parameters
 * @returns true if the cell is disabled, false otherwise
 */
export const isCellDisabled = (params: any): boolean => {
  const editableWhen = params.data?.benefit?.editableWhen;

  if (!editableWhen) {
    return false;
  }

  const dependentValue = resolveDependentValue(params, editableWhen.dependsOn);
  const parentUnanswered =
    dependentValue === undefined ||
    dependentValue === null ||
    dependentValue === "";

  return (
    (parentUnanswered && !params.data?.benefit?.isRequired) ||
    editableWhen.disabledValues.includes(dependentValue as string)
  );
};

/**
 * Returns cellClassRules for validation error state
 * Extracted to avoid repetition across column definitions
 */
export const getValidationCellClassRules = (): Record<
  string,
  (params: CellClassParams) => boolean
> => getCellClassesRules();

/**
 * Processes coverages array and returns coverage where type property matches
 *
 * @param type - Coverage type identifier to search for
 * @param data - Data object containing coverages array
 * @returns Found coverage or undefined
 */
export const getCoverageByType = (
  type: string,
  data: { coverages: RfpCoverage[] },
): RfpCoverage | undefined => {
  if (!data?.coverages || !Array.isArray(data.coverages)) {
    return undefined;
  }

  return data.coverages.find((coverage: RfpCoverage) => coverage.type === type);
};

/**
 * Determines if a row is a child/property row
 *
 * Child rows have a propertyKey field, indicating they are a property
 * under a parent benefit definition.
 *
 * @param params - AG Grid cell params
 * @returns True if the row is a child row, false otherwise
 */
export const isChildRow = (params: any): boolean =>
  params?.data?.propertyKey !== undefined;

/**
 * Returns an array of CSS class names for a cell based on review and comment status.
 *
 * @param params - AG Grid cell params
 * @param identifier - Unique identifier for the cell
 * @param zebraClassNames - Array of zebra stripe class names
 * @returns Array of class names
 */
export const getCellClasses = (
  params: any,
  identifier: string,
  zebraClassNames: string[],
): string[] => {
  // Compose class names
  // NOTE: Only static classes are returned here.
  //    Classes that depend on dynamic state (validation errors) are handled by cellClassRules
  const excelRowClass = isChildRow(params) ? "" : CLASSNAMES.EXCEL_PARENT_ROW;

  return [
    `${CLASSNAMES.IDENTIFIER}-${identifier}`,
    `${CLASSNAMES.BENEFIT_KEY}-${params.data.benefitKey}`,
    `${params.data.benefitKey}-${identifier}`,
    CLASSNAMES.HEADER_COLUMN_LEVEL_2,
    CLASSNAMES.BENEFIT_CELL,
    CLASSNAMES.EXCEL_CENTER_ALIGN,
    excelRowClass,
    ...zebraClassNames,
  ].filter(Boolean);
};

/**
 * Creates network column definitions for a specific plan
 *
 * @param plan - Benefit plan
 * @param networks - Available network options
 * @param zebraClassNames - Array of zebra stripe class names
 * @returns Array of network column definitions
 */
export const createNetworkColumns = (
  plan: RfpBenefitPlan,
  networks: readonly string[],
  zebraClassNames: string[],
): ColDef[] =>
  networks.map((networkName: string, index) => ({
    field: `${plan.identifier}.${networkName}`, // keeps sorting/filtering consistent
    headerName: networkName,
    suppressHeaderContextMenu: true,
    // suppressMovable: true, // Prevent dragging network sub-headers
    headerClass: [
      CLASSNAMES.HEADER_COLUMN_LEVEL_2,
      CLASSNAMES.BENEFIT_CELL,
      ...zebraClassNames,
    ],
    cellClass: (params) =>
      getCellClasses(
        params,
        `${plan.identifier}.${networkName}`,
        zebraClassNames,
      ),
    cellClassRules: getCellClassesRules(),
    headerTooltip: `${plan.identifier}.${networkName}`,
    editable: (params) => isEditable(params),
    valueFormatter: (params) => valueFormatter(params),
    cellRendererSelector: (params) => cellRendererSelector(params),
    cellEditorSelector: (params) => cellEditorSelector(params),
    // Dynamically get and set value based on networkDependent
    valueGetter: (params) => valueGetter(params),
    valueSetter: (params) => valueSetter(params),
    // Dynamically control column span
    colSpan: (params) => {
      const _isNetworked = params.data?.benefit?.networkDependent;
      return _isNetworked ? 1 : index === 0 ? networks.length : 0;
    },
  }));

/**
 * Creates the static benefits column definition
 * This column is always pinned to the left and shows benefit labels
 *
 * @returns Benefits column definition
 */
export const createBenefitsColumn = (): ColDef => ({
  lockPosition: "left", // Always keep Benefits column at the left
  lockPinned: true,
  lockVisible: true,
  pinned: "left",
  field: "benefit.label",
  headerName: "Coverage Opportunity",
  headerClass: "benefits-header",
  flex: 0,
  width: 200,
  // Keep in sync with the RatesGrid/SummaryGrid left-column minWidth so the
  // aligned grids can follow this column all the way down.
  minWidth: 100,
  cellClass: (params) => {
    const isNested = params.data?.benefit?.nested === true;
    const excelRowClass = isChildRow(params) ? "" : CLASSNAMES.EXCEL_PARENT_ROW;
    const baseClasses = isNested
      ? [CLASSNAMES.BENEFIT_LABEL_CELL, CLASSNAMES.BENEFIT_LABEL_CELL_NESTED]
      : [CLASSNAMES.BENEFIT_LABEL_CELL];
    return [...baseClasses, excelRowClass].filter(Boolean);
  },
  tooltipValueGetter: (params: ITooltipParams) => params.value as string, // Show full label text on hover
  headerTooltip: "Available benefits",
  editable: false,
  suppressMovable: true,
  suppressHeaderContextMenu: true,
  suppressNavigable: true,
});

// ---------------------------------------------------------------------------
// Cell renderer selector — wraps the base cellRendererSelector with optional
// component support. Exported so column build strategies can use it directly.
// ---------------------------------------------------------------------------

export type CellRendererComponent = ComponentType<
  PropsWithChildren<ICellRendererParams>
>;

/**
 * Returns a cellRendererSelector that first applies benefit-spec-driven
 * rendering, then falls back to the provided CellRenderer component (if any).
 */
export const getCellRendererSelector =
  (CellRenderer?: CellRendererComponent) =>
  (params: ICellRendererParams): ReturnType<typeof cellRendererSelector> => {
    const result = cellRendererSelector(params);
    if (result) {
      return result;
    }

    if (!CellRenderer) {
      return undefined;
    }

    return {
      // displayName stays stable across prod minification, while function names may not.
      component: CellRenderer.displayName ?? CellRenderer.name,
      params: { ...params, config: {}, cellEditorParams: {} },
    };
  };
